import { useAuth0 } from '@auth0/auth0-react'
import { IconChevronLeft, IconChevronRight, IconX } from '@tabler/icons-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import { Mousewheel, Navigation } from 'swiper/modules'
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react'
import RestaurantCard from '../../components/restaurant/RestaurantCard'
import ImpressionTracker from '../analytics/ImpressionTracker'
import { logEvent } from '../../analytics/analytics'
import { ROUTES } from '../../helpers/routes-helper'
import { setRecommendationAttribution } from '../../helpers/recommendationAttribution'
import { mapRestaurantsWithPredictions } from '../../helpers/restaurant-helpers'
import { DEFAULT_CARD_IMAGE } from '../../helpers/static-constants'
import useAppUtilities from '../../hooks/useAppUtilities'
import { MuiAlert, MuiSnackbar } from '../../pages/eater/styled/global.styled'
import { useAppSelector } from '../../store'
import {
  selectMessage,
  selectSeverity,
  selectShouldOpen,
  setOpen,
} from '../../store/restaurantSlice'
import {
  ILandingRestaurantList,
  LandingType,
  LandingTypeValue,
} from '../../types'
import RestaurantCardContainerSkeleton from '../skeleton/RestaurantCardContainerSkeleton'
import {
  RestaurantCardContainerNoDataTypography,
  RestaurantCardContainerSwiperContainer,
  RestaurantCardContainerTopBarContainer,
  RestaurantCardContainerTopBarInnerContainer,
  RestaurantCardContainerTopBarTypography,
  RestaurantCardContainerTopBarViewAllTypography,
} from '../styled/restaurant/RestaurantCardContainer.styled'
import ImgIcon from '../ui/ImgIcon'

interface RestaurantCardContainerProps {
  title: string
  cardList: ILandingRestaurantList | undefined
  loading: boolean
  name: LandingType
  isHybrid: boolean
  recommendationId?: string
  modelVersion?: string
}

const RestaurantCardContainer: React.FC<RestaurantCardContainerProps> = ({
  title,
  cardList,
  loading = false,
  name,
  recommendationId,
  modelVersion,
}) => {
  const { theme, navigate, dispatch, isSmallScreen } = useAppUtilities()
  // eslint-disable-next-line
  const { isAuthenticated, isLoading } = useAuth0()
  const message = useAppSelector(selectMessage)
  const severity = useAppSelector(selectSeverity)
  const openServer = useAppSelector(selectShouldOpen)
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set())
  const swiperRef = useRef<SwiperRef>(null)
  const viewedRecoRef = useRef<string | null>(null)

  // "Not interested" affordance on reco cards — emits recommendation_rejected and hides the card
  // locally. Low-risk: purely client-side, no refetch.
  const handleDismiss = useCallback(
    (restaurantId: string | undefined, index: number): void => {
      if (!restaurantId) return
      logEvent('recommendation_rejected', {
        recommendation_id: recommendationId ?? '',
        restaurant_id: restaurantId,
        rank_position: index,
        reason: 'not_interested',
        list_type: name,
        model_version: modelVersion,
        screen_name: 'home',
      })
      setDismissedIds((prev) => {
        const next = new Set(prev)
        next.add(restaurantId)
        return next
      })
    },
    [recommendationId, modelVersion, name],
  )

  useEffect(() => {
    const cards = cardList ? mapRestaurantsWithPredictions(cardList) : []
    const restaurantCount = cards.length
    if (!loading && recommendationId && restaurantCount > 0) {
      const key = `${recommendationId}:${name}`
      if (viewedRecoRef.current !== key) {
        viewedRecoRef.current = key
        // items = the ranked restaurant IDs actually shown (backend REQUIRED_PROP for reco_view).
        const items = cards.map((c) => c?.restaurantId ?? '').filter(Boolean)
        logEvent('recommendation_view', {
          recommendation_id: recommendationId,
          items,
          list_type: name,
          result_count: restaurantCount,
          model_version: modelVersion,
          screen_name: 'home',
        })
      }
    }
  }, [loading, recommendationId, name, cardList, modelVersion])

  const handlePrev = (): void => {
    if (swiperRef.current?.swiper) {
      swiperRef.current.swiper.slidePrev()
    }
  }

  const handleNext = (): void => {
    if (swiperRef.current?.swiper) {
      swiperRef.current.swiper.slideNext()
    }
  }

  const handleSlideChange = (swiper: any): void => {
    setIsBeginning(swiper.isBeginning)
    setIsEnd(swiper.isEnd)
  }

  const setOpenServer = (): void => {
    dispatch(setOpen(false))
  }

  const FallbackMessage = useCallback(
    () => (
      <div>
        <RestaurantCardContainerNoDataTypography>
          We cannot find any {LandingTypeValue[name]} with your filters
        </RestaurantCardContainerNoDataTypography>
      </div>
    ),
    [name],
  )

  // Always use restaurants, with predictions attached as optional metadata
  const restaurantsWithPredictions = useMemo(() => {
    if (!cardList) {
      return []
    }
    return mapRestaurantsWithPredictions(cardList)
  }, [cardList])

  const slidesWithAds = useMemo(() => {
    const slides: React.ReactNode[] = []

    restaurantsWithPredictions.forEach((card, index) => {
      if (card.restaurantId && dismissedIds.has(card.restaurantId)) {
        return
      }
      // Restaurant slide (always rendered)
      slides.push(
        <SwiperSlide
          key={card.restaurantId ?? `restaurant-${index}`}
          virtualIndex={index}
        >
          <RestaurantCardContainerSwiperContainer
            style={{ position: 'relative' }}
            onClick={() => {
              logEvent('restaurant_click', {
                restaurant_id: card?.restaurantId ?? '',
                rank_position: index,
                recommendation_id: recommendationId,
                list_type: name,
                screen_name: 'home',
              })
              if (recommendationId) {
                logEvent('recommendation_accepted', {
                  recommendation_id: recommendationId,
                  restaurant_id: card?.restaurantId ?? '',
                  rank_position: index,
                  action: 'click',
                  list_type: name,
                  model_version: modelVersion,
                  screen_name: 'home',
                })
                // Durable attribution fallback: persist the recommendation + rank keyed by alias so
                // the detail page can still attribute restaurant_viewed / reservation_click even when
                // nav state is lost (reload, deep-link re-entry, back/forward restore).
                setRecommendationAttribution(card?.alias, {
                  recommendationId,
                  rankPosition: index,
                  listType: name,
                  modelVersion,
                })
              }
              // Thread the exact rank + recommendation the user came from into the detail
              // route via nav state, so restaurant_viewed / reservation_click can attribute
              // the precise rank (additive — the navigation path itself is unchanged).
              navigate(`${ROUTES.RESTAURANT_DETAILS}/${card?.alias}/details`, {
                state: {
                  recommendation_id: recommendationId,
                  rank_position: index,
                  list_type: name,
                  model_version: modelVersion,
                  source: 'home',
                },
              })
            }}
          >
            {recommendationId && (
              <button
                type="button"
                aria-label="Not interested"
                title="Not interested"
                onClick={(e) => {
                  e.stopPropagation()
                  handleDismiss(card?.restaurantId, index)
                }}
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 20,
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  border: 'none',
                  background: 'rgba(0, 0, 0, 0.45)',
                  color: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                <IconX size={14} stroke={2} />
              </button>
            )}
            <ImpressionTracker
              onImpress={() => {
                logEvent('restaurant_impression', {
                  restaurant_id: card?.restaurantId ?? '',
                  rank_position: index,
                  recommendation_id: recommendationId,
                  list_type: name,
                  screen_name: 'home',
                })
              }}
              onExit={(dwellMs) => {
                logEvent('restaurant_impression', {
                  restaurant_id: card?.restaurantId ?? '',
                  rank_position: index,
                  recommendation_id: recommendationId,
                  dwell_ms: dwellMs,
                  list_type: name,
                  screen_name: 'home',
                })
              }}
            >
              <RestaurantCard
                id={card?.restaurantId}
                alias={card?.alias}
                imgList={[
                  card?.imageUrl && card.imageUrl !== ''
                    ? card.imageUrl
                    : DEFAULT_CARD_IMAGE,
                ]}
                title={card?.name}
                subTitle={card?.description ?? ''}
                distance={`${card?.distance ?? 0}`}
                isVerified={card?.verified ?? false}
                tagChipData={
                  typeof card.predictionYes === 'number'
                    ? [
                        {
                          percentage: card.predictionYes.toFixed(2),
                          title: 'Match',
                        },
                      ]
                    : undefined
                }
                tagChipBgColor={'orange'}
                recommendationSummary={card?.recommendationSummary}
                recommendationId={recommendationId}
                modelVersion={modelVersion}
                phone={card?.phone}
                latitude={card?.lat}
                longitude={card?.lng}
                placeId={card?.placeId}
                address={[card?.address1, card?.city, card?.state]
                  .filter(Boolean)
                  .join(', ')}
                reservationUrl={card?.reservationUrl}
                website={card?.website}
                cuisine={card?.cuisine ?? ''}
                city={card?.city ?? ''}
                rankPosition={index}
                plannerSourceSurface="home"
              />
            </ImpressionTracker>
          </RestaurantCardContainerSwiperContainer>
        </SwiperSlide>,
      )
    })

    return slides
  }, [
    restaurantsWithPredictions,
    navigate,
    recommendationId,
    modelVersion,
    name,
    dismissedIds,
    handleDismiss,
  ])

  return (
    <div>
      <RestaurantCardContainerTopBarContainer>
        <div>
          <RestaurantCardContainerTopBarTypography>
            {title}
          </RestaurantCardContainerTopBarTypography>
        </div>
        {!loading && cardList && cardList.restaurants.length > 0 && (
          <RestaurantCardContainerTopBarInnerContainer>
            <RestaurantCardContainerTopBarViewAllTypography
              onClick={() =>
                // Thread the recommendation + list identity into the "View All" page so its ranked
                // reco surface is attributable to the same recommendation as this carousel (it's the
                // paginated continuation of the same set), instead of arriving with a blank id.
                navigate(`${ROUTES.RESTAURANTS}?type=${cardList.type}`, {
                  state: {
                    recommendation_id: recommendationId,
                    list_type: name,
                    model_version: modelVersion,
                  },
                })
              }
            >
              View All
            </RestaurantCardContainerTopBarViewAllTypography>
            {!isSmallScreen && (
              <>
                <ImgIcon
                  iconElement={<IconChevronLeft stroke={2} />}
                  alt="left-swipe-restaurant"
                  imgSize={18}
                  wrapperStyle={{
                    backgroundColor: theme.palette.secondary.contrastText,
                    height: '36px',
                    width: '36px',
                    outline: `2px solid ${theme.palette.grey[200]}`,
                    marginRight: '10px',
                    cursor: isBeginning ? 'not-allowed' : 'pointer',
                    opacity: isBeginning ? 0.5 : 1,
                  }}
                  onClick={isBeginning ? undefined : handlePrev}
                />
                <ImgIcon
                  iconElement={<IconChevronRight stroke={2} />}
                  alt="right-swipe-restaurant"
                  imgSize={18}
                  wrapperStyle={{
                    backgroundColor: theme.palette.secondary.contrastText,
                    height: '36px',
                    width: '36px',
                    outline: `2px solid ${theme.palette.grey[200]}`,
                    cursor: isEnd ? 'not-allowed' : 'pointer',
                    opacity: isEnd ? 0.5 : 1,
                  }}
                  onClick={isEnd ? undefined : handleNext}
                />
              </>
            )}
          </RestaurantCardContainerTopBarInnerContainer>
        )}
      </RestaurantCardContainerTopBarContainer>

      {!loading && !isLoading ? (
        <div style={{ paddingTop: '6px' }}>
          {restaurantsWithPredictions.length > 0 ? (
            <Swiper
              ref={swiperRef}
              slidesPerView={3.4}
              spaceBetween={10}
              navigation={false}
              mousewheel={false}
              modules={[Mousewheel, Navigation]}
              onSlideChange={handleSlideChange}
              className="mySwiper"
              grabCursor={true}
              breakpoints={swiperBreakPoints}
            >
              {slidesWithAds}
            </Swiper>
          ) : (
            <FallbackMessage />
          )}
        </div>
      ) : (
        <RestaurantCardContainerSkeleton />
      )}
      <MuiSnackbar open={openServer} onClose={setOpenServer}>
        <MuiAlert onClose={setOpenServer} severity={severity ?? 'success'}>
          {message}
        </MuiAlert>
      </MuiSnackbar>
    </div>
  )
}

export default RestaurantCardContainer

const swiperBreakPoints = {
  '0': {
    slidesPerView: 1,
    spaceBetween: 5,
  },
  '336': {
    slidesPerView: 1.1,
    spaceBetween: 5,
  },
  '370': {
    slidesPerView: 1.2,
    spaceBetween: 5,
  },
  '410': {
    slidesPerView: 1.3,
    spaceBetween: 5,
  },
  '430': {
    slidesPerView: 1.4,
    spaceBetween: 5,
  },
  '470': {
    slidesPerView: 1.6,
    spaceBetween: 5,
  },
  '500': {
    slidesPerView: 1.7,
    spaceBetween: 5,
  },
  '530': {
    slidesPerView: 1.8,
    spaceBetween: 5,
  },
  '570': {
    slidesPerView: 1.9,
    spaceBetween: 5,
  },
  '600': {
    slidesPerView: 2,
    spaceBetween: 5,
  },
  '630': {
    slidesPerView: 2.1,
    spaceBetween: 5,
  },
  '670': {
    slidesPerView: 2.2,
    spaceBetween: 5,
  },
  '710': {
    slidesPerView: 2.4,
    spaceBetween: 5,
  },
  '740': {
    slidesPerView: 2.5,
    spaceBetween: 5,
  },
  '770': {
    slidesPerView: 2.6,
    spaceBetween: 5,
  },
  '800': {
    slidesPerView: 2.7,
    spaceBetween: 5,
  },
  '845': {
    slidesPerView: 2.8,
    spaceBetween: 5,
  },
  '890': {
    slidesPerView: 3,
    spaceBetween: 5,
  },
  '930': {
    slidesPerView: 3.2,
    spaceBetween: 5,
  },
  '960': {
    slidesPerView: 2.5,
    spaceBetween: 5,
  },
  '975': {
    slidesPerView: 2.6,
    spaceBetween: 10,
  },
  '1023': {
    slidesPerView: 2.7,
    spaceBetween: 10,
  },
  '1090': {
    slidesPerView: 2.8,
    spaceBetween: 5,
  },
  '1130': {
    slidesPerView: 3,
    spaceBetween: 5,
  },
  '1200': {
    slidesPerView: 3.2,
    spaceBetween: 5,
  },
  '1250': {
    slidesPerView: 3.4,
    spaceBetween: 5,
  },
}
